import clsx from 'clsx'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import React, {
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import SvgCheck from '@Assets/SVG/Check'
import { useTranslation } from '@Globals/Translations'

import styles from './SelectInput.module.scss'

export type SelectInputValidationResult = { errorMessage: string, success: boolean }
export type SelectInputValidationFn = (value: string) => SelectInputValidationResult
export type SelectInputActionProps = {
  touch: () => void
  validate: (value?: string) => boolean | undefined
}

export const useSelectInputCustomActionRef = () => {
  const ref = useRef<SelectInputActionProps | null>(null)
  return ref
}
interface SelectInputCustomProps {
  actionRef?: MutableRefObject<SelectInputActionProps | null>
  ariaLabel?: string
  className?: string
  customElement: (value: string) => ReactNode
  defaultTouched?: boolean
  disabled?: boolean
  disabledOptions?: string[]
  id?: string
  label?: string | null
  onChange: (value: string, valid: boolean) => void
  options?: string[]
  setValue?: Dispatch<SetStateAction<string>> | ((value: string) => void)
  showValid?: boolean
  validations?: Array<SelectInputValidationFn>
  value: string
}
export const SelectInputCustom: FC<PropsWithChildren<SelectInputCustomProps>> = ({
  actionRef,
  ariaLabel,
  className,
  customElement,
  disabled,
  label,
  value,
  disabledOptions = [],
  id,
  onChange,
  validations = [],
  setValue,
  defaultTouched = false,
  options,
  showValid = true,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [touched, setTouched] = useState(defaultTouched)
  const [errors, setErrors] = useState<Array<SelectInputValidationResult>>([])
  const hasError = useMemo(() => touched && validations.length > 0 && errors.length > 0, [errors, touched, validations.length])
  const isValid = useMemo(() => touched && validations.length > 0 && errors.length === 0, [errors, touched, validations.length])

  const runValidations: (newValue?: string) => boolean = useCallback(
    (newValue?: string) => {
      if (!isNil(newValue) && setValue) {
        setValue(newValue)
      }
      const validationErrors: Array<SelectInputValidationResult> = []
      forEach(validations, (validationFn) => {
        const result: SelectInputValidationResult = validationFn(newValue || value)
        if (!result?.success) {
          validationErrors.push(result)
        }
      })
      setErrors(validationErrors)
      setIsOpen(false)
      return validationErrors.length === 0
    },
    [value, validations, setValue],
  )

  useImperativeHandle(actionRef, () => ({
    validate: runValidations,
    touch: () => setTouched(true),
  }))

  const handleChange = useCallback((newValue: string) => {
    setTouched(true)
    setIsOpen(false)
    onChange(newValue, runValidations(newValue))
  }, [onChange, runValidations])

  useEffect(() => {
    if (!isEmpty(value)) {
      runValidations()
    }
    if (defaultTouched) {
      runValidations(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleWindowClick = (event) => {
      if (ref.current && !ref.current!.contains(event.target)) {
        setIsOpen(false)
      }
    }
    window.addEventListener('click', handleWindowClick)
    return () => {
      window.removeEventListener('click', handleWindowClick)
    }
  }, [])

  return (
    <div
      className={clsx(
        styles.inputContainer,
        hasError && styles.error,
        isValid && showValid && styles.success,
        className,
      )}
      ref={ref}
    >
      <input
        aria-label={typeof ariaLabel === 'string' ? ariaLabel : undefined}
        className={clsx(styles.inputField, !isEmpty(value) && styles.inputFieldWithValue, !isNil(label) && styles.withLabel)}
        disabled={disabled}
        id={id}
        onClick={() => {
          setIsOpen(prev => !prev)
        }}
        type="button"
      />
      <span className={styles.hoverValue}>{customElement(value)}</span>
      {isOpen && (
        <div className={styles.dropdown} style={{ minWidth: `${ref.current?.offsetWidth}px`, maxWidth: `${ref.current?.offsetWidth}px` }}>
          {(options || []).map(option => (
            <button key={`${id}_${option}`} disabled={disabledOptions.includes(option)} onClick={() => handleChange(option)} type="button">
              {customElement ? customElement(option) : option}
            </button>
          ))}
        </div>
      )}
      {!isEmpty(label) && (
        <label
          className={clsx(
            styles.inputLabel,
            !!value && styles.labelUp,
          )}
          htmlFor={id}
          id={`${id}-label`}
        >
          {label}
        </label>
      )}
      <div className={clsx(styles.arrow, disabled && styles.disabled)}>
        {(!showValid || !isValid) && (
          <SvgAngleLeft className={clsx(
            !!isOpen && styles.isOpen,
            !isOpen && styles.isClosed,
          )}
          />
        )}
      </div>
      {showValid && isValid && <div className={styles.checkmark}><SvgCheck /></div>}
      {hasError && (
        <span className={clsx(styles.error, 'error')}>
          {t('common:ERROR_LABEL')}
          {': '}
          {t(first(errors)!.errorMessage)}
        </span>
      )}
    </div>
  )
}
